import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import WizardLayout from "../components/WizardLayout";
import SmartQuestionCard from "../components/rfq/SmartQuestionCard";
import QuestionProgress from "../components/rfq/QuestionProgress";
import RFQAnalysisLoader from "../components/rfq/RFQAnalysisLoader";
import { Button } from "../components/ui/button";
import { ArrowLeft, Check } from "lucide-react";
import { confirmRfqField, getStructuredRfq, buildConfirmationState, saveBuyerSession } from "../lib/api";
import { QUESTIONS } from "../constants/testIds";

export default function SmartQuestions() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState(location.state?.intelligence || null);
  const [history, setHistory] = useState([]); // previous questions for back nav

  const load = async () => {
    setLoading(true);
    try {
      const data = await getStructuredRfq(id);
      setState(data);
      if (data.buyer_complete) {
        if (data.session_token) saveBuyerSession(data);
        navigate(`/rfq/${id}/confirmation`, {
          replace: true,
          state: buildConfirmationState(data),
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load questions");
      navigate("/rfq/new");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (state?.next_question && state?.rfq_id === id) {
      setLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const question = state?.next_question;
  const answered = useMemo(
    () => (state?.structured_fields ? Object.values(state.structured_fields).filter((v) => v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)).length : 0),
    [state]
  );
  const remaining = (state?.missing_fields?.length || 0) + (state?.low_confidence_fields?.length || 0);

  const handleAnswer = async (value) => {
    if (!question || submitting) return;
    setSubmitting(true);
    try {
      setHistory((h) => [...h, { question, snapshot: state }]);
      const res = await confirmRfqField(id, {
        field: question.field,
        value,
        question_id: question.question_id,
      });
      setState(res);
      if (res.buyer_complete) {
        if (res.session_token) saveBuyerSession(res);
        toast.success("Your product request is ready.");
        navigate(`/rfq/${id}/confirmation`, { state: buildConfirmationState(res) });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save answer");
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    if (!history.length) {
      navigate("/rfq/new");
      return;
    }
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    // Restore previous question UI; answers remain on server (no destructive undo of confirmed fields)
    setState((s) => ({
      ...s,
      next_question: prev.question,
      buyer_complete: false,
    }));
  };

  if (loading) {
    return (
      <WizardLayout
        stepIndex={1}
        totalSteps={3}
        eyebrow="Clarifying"
        title="Preparing questions"
        subtitle="Only asking for what we still need."
      >
        <RFQAnalysisLoader message="Preparing your next question..." />
      </WizardLayout>
    );
  }

  if (state?.buyer_complete) {
    return (
      <WizardLayout
        stepIndex={2}
        totalSteps={3}
        eyebrow="Complete"
        title="Your product request is ready."
        subtitle="We are preparing manufacturer matching."
      >
        <div data-testid={QUESTIONS.complete} className="space-y-6">
          <p className="text-neutral-600">Redirecting to confirmation…</p>
          <Button
            onClick={() =>
              navigate(`/rfq/${id}/confirmation`, { state: buildConfirmationState(state) })
            }
            className="btn-wizard-accent"
          >
            <Check className="w-4 h-4 mr-2" /> View confirmation
          </Button>
        </div>
      </WizardLayout>
    );
  }

  return (
    <WizardLayout
      stepIndex={1}
      totalSteps={3}
      eyebrow="One quick question"
      title="Help us complete your request"
      subtitle="We only ask for details that are still missing."
      footer={
        <div className="flex items-center justify-between" data-testid={QUESTIONS.container}>
          <Button
            variant="ghost"
            onClick={goBack}
            data-testid={QUESTIONS.backBtn}
            className="rounded-sm h-11 px-4 text-neutral-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <span className="text-xs text-neutral-400 uppercase tracking-[0.2em]">
            One question at a time
          </span>
        </div>
      }
    >
      <QuestionProgress answered={answered} remaining={remaining} />
      <SmartQuestionCard
        key={question?.question_id || "q"}
        question={question}
        onSelect={handleAnswer}
        disabled={submitting}
      />
    </WizardLayout>
  );
}
